/** Normalise `process.type` renvoyé par l'API (enum string ou `{ value }`). */
export function parseProcessType(raw: unknown): string {
  if (raw == null || raw === '') return '';
  if (typeof raw === 'string') return raw.trim().toUpperCase();
  if (typeof raw === 'object' && raw !== null) {
    const value = (raw as { value?: unknown }).value;
    if (value != null && value !== '') return String(value).trim().toUpperCase();
  }
  return String(raw).trim().toUpperCase();
}

/** Pas d'appels POST /reference/ pour cartographies issues d'un fichier ou d'une image. */
export function shouldSkipReferenceApi(processType: string): boolean {
  const normalized = (processType || '').trim().toUpperCase();
  return normalized === 'IMAGE' || normalized === 'HUMAN';
}

export type ProcessMethodKind = 'MANUAL' | 'AI' | 'HUMAN' | 'IMAGE' | 'UNKNOWN';

export function getProcessMethodKind(raw: unknown): ProcessMethodKind {
  const type = parseProcessType(raw);
  if (type === 'MANUAL' || type === 'AI' || type === 'HUMAN' || type === 'IMAGE') return type;
  return 'UNKNOWN';
}

export type ProcessMethodDisplay = {
  kind: ProcessMethodKind;
  labelKey: string;
  detailKey: string;
  badgeClassSuffix: string;
  iconClassName: string;
};

const PROCESS_METHOD_DISPLAY: Record<ProcessMethodKind, ProcessMethodDisplay> = {
  MANUAL: {
    kind: 'MANUAL',
    labelKey: 'views.processView.homeTypeManual',
    detailKey: 'views.processView.homeDetailManual',
    badgeClassSuffix: 'manual',
    iconClassName: 'fa-solid fa-pen',
  },
  AI: {
    kind: 'AI',
    labelKey: 'views.processView.homeTypeAI',
    detailKey: 'views.processView.homeMethodInterviewBlurb',
    badgeClassSuffix: 'ai',
    iconClassName: 'fa-regular fa-message',
  },
  HUMAN: {
    kind: 'HUMAN',
    labelKey: 'views.processView.homeTypeHuman',
    detailKey: 'views.processView.homeMethodUploadTextBlurb',
    badgeClassSuffix: 'human',
    iconClassName: 'fa-regular fa-file-lines',
  },
  IMAGE: {
    kind: 'IMAGE',
    labelKey: 'views.processView.homeTypeImage',
    detailKey: 'views.processView.homeMethodUploadImageBlurb',
    badgeClassSuffix: 'image',
    iconClassName: 'fa-regular fa-image',
  },
  UNKNOWN: {
    kind: 'UNKNOWN',
    labelKey: 'views.processView.homeTypeProcessMap',
    detailKey: '',
    badgeClassSuffix: 'process',
    iconClassName: 'fa-regular fa-message',
  },
};

export function getProcessMethodDisplay(raw: unknown): ProcessMethodDisplay {
  return PROCESS_METHOD_DISPLAY[getProcessMethodKind(raw)];
}

export function getProcessCardDetailLine(
  process: { description?: string | null; type?: unknown },
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const description = process.description && String(process.description).trim();
  if (description) return description;

  const display = getProcessMethodDisplay(process.type);
  if (display.detailKey) return t(display.detailKey);

  return t('views.processView.homeInterviewCount', { count: 1 });
}

export function isManualProcess(raw: unknown): boolean {
  return getProcessMethodKind(raw) === 'MANUAL';
}
